import { Body1, Button, Caption1, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertType } from '../../libs/models/AlertType';
import { useUserMemory } from '../../libs/hooks/useUserMemory';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { MemoryDeleteConfirmDialog } from './MemoryDeleteConfirmDialog';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    note: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingVerticalS,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    noteText: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        // Long notes and slugs must wrap instead of stretching the row.
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
    hint: {
        color: tokens.colorNeutralForeground3,
    },
});

/**
 * The stored per-user long-term-memory notes (#2220) with their per-note and delete-all controls —
 * the transparency + deletion surface. Lives on its own page (MemoriesView) so the notes don't crowd
 * Settings; the opt-in toggle stays in Settings (UserMemorySection). Fetches the current state on mount.
 */
export const UserMemoryNotes: React.FC = () => {
    const classes = useClasses();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const memory = useAppSelector((state: RootState) => state.app.userMemory);
    const { fetchMemory, deleteNote, deleteAll } = useUserMemory();

    const [initialized, setInitialized] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState<{ mode: 'one' | 'all'; id?: string } | null>(null);

    // The hook recreates fetchMemory on every silent token renewal, so this effect re-runs while the
    // user sits on the page. Track first-load completion in a ref (readable inside the async callback
    // without re-triggering the effect) so a background refetch never re-shows the spinner or, on a
    // transient failure, blanks an already-rendered list.
    const initializedRef = useRef(false);

    // Latest store note count, readable inside the async callback without adding `memory` to the effect
    // deps. Notes seeded at app load (useInitialUserMemory) or by the sidebar's visibility gate may
    // already be present before this page's own first GET resolves.
    const notesLenRef = useRef(memory.notes.length);
    notesLenRef.current = memory.notes.length;

    useEffect(() => {
        let active = true;
        void fetchMemory().then((result) => {
            if (!active) {
                return;
            }
            if (result === undefined) {
                // Surface the load error only on the first load AND only when there's genuinely nothing
                // to show. Notes already in the store (seeded before this page mounted) must survive a
                // transient first-GET blip — this page is their delete surface, so blanking it behind an
                // error would strand the very data the user came to remove. A failed background refetch
                // likewise leaves the last good list untouched.
                if (!initializedRef.current && notesLenRef.current === 0) {
                    setLoadError(true);
                }
            } else {
                setLoadError(false);
            }
            initializedRef.current = true;
            setInitialized(true);
        });
        return () => {
            active = false;
        };
    }, [fetchMemory]);

    const onConfirmDelete = () => {
        if (!dialog) {
            return;
        }

        // Resolve which action to run. A per-note ('one') delete needs the note id; bail rather than
        // substituting '' (which would issue DELETE memory/me/ with an empty id segment). Unreachable
        // today — every { mode: 'one' } dialog is opened with an id — but this fails fast otherwise.
        let operation: Promise<void>;
        if (dialog.mode === 'all') {
            operation = deleteAll();
        } else if (dialog.id !== undefined) {
            operation = deleteNote(dialog.id);
        } else {
            setDialog(null);
            return;
        }

        setBusy(true);
        operation
            .catch(() => {
                // The hook logs + reverts. On a data-removal control the user must be able to tell the
                // action didn't happen — the note reappears — so surface an error.
                dispatch(addAlert({ message: t('settings.memory.deleteError'), type: AlertType.Error }));
            })
            .finally(() => {
                setBusy(false);
                setDialog(null);
            });
    };

    return (
        <div className={classes.root}>
            {!initialized && <Spinner size="tiny" labelPosition="after" label={t('settings.memory.loading')} />}

            {/* Surface the load error only when there's nothing else to show. If the store already holds
                notes (seeded before this page mounted), render them below instead of blanking the page
                behind the error — otherwise a transient first-GET blip would hide the delete surface. */}
            {initialized && loadError && memory.notes.length === 0 && (
                <Body1 className={classes.hint}>{t('settings.memory.error')}</Body1>
            )}

            {initialized && !loadError && memory.notes.length === 0 && !memory.optedIn && (
                <Body1 className={classes.hint}>{t('settings.memory.emptyOptedOut')}</Body1>
            )}

            {initialized && !loadError && memory.notes.length === 0 && memory.optedIn && (
                <Body1 className={classes.hint}>{t('settings.memory.empty')}</Body1>
            )}

            {/* Render the stored notes and their delete controls whenever notes exist, regardless of the
                opt-in flag. The flag is person-global but an opt-out purges only the current company's
                scope, so GET /memory/me can legitimately return optedIn:false with residual notes from
                another company; gating the list on optedIn would hide those notes and every delete
                control, defeating the transparency/deletion surface. The empty-state hints above stay
                opt-in-aware. The list itself is not gated on !loadError: notes already in the store
                must render even if this page's own first GET blipped. */}
            {initialized && memory.notes.length > 0 && (
                <>
                    <div className={classes.toolbar}>
                        <Button
                            appearance="subtle"
                            size="small"
                            disabled={busy}
                            onClick={() => {
                                setDialog({ mode: 'all' });
                            }}
                            data-testid="memoryDeleteAll"
                        >
                            {t('settings.memory.deleteAll')}
                        </Button>
                    </div>
                    <div className={classes.list}>
                        {memory.notes.map((note) => (
                            <div key={note.id} className={classes.note} data-testid="memoryNote">
                                {/* Rendered as plain text — React escapes it, so a note can never inject
                                    markup even though its content is model-authored. */}
                                <div className={classes.noteText}>
                                    <Text weight="semibold">{note.description || note.id}</Text>
                                    <Caption1>{note.content}</Caption1>
                                </div>
                                {/* Unique accessible name per note so a screen reader can tell the delete
                                    buttons apart; the description is visible beside the button, so the
                                    hover title stays generic. */}
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<DeleteRegular />}
                                    disabled={busy}
                                    aria-label={t('settings.memory.deleteNoteNamed', {
                                        name: note.description || note.id,
                                    })}
                                    title={t('settings.memory.deleteNote')}
                                    onClick={() => {
                                        setDialog({ mode: 'one', id: note.id });
                                    }}
                                    data-testid="memoryDeleteNote"
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}

            <MemoryDeleteConfirmDialog
                open={dialog !== null}
                mode={dialog?.mode ?? 'one'}
                busy={busy}
                onConfirm={onConfirmDelete}
                onCancel={() => {
                    if (!busy) {
                        setDialog(null);
                    }
                }}
            />
        </div>
    );
};
